import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

// Tab Navigation Component from employee profile
import ProfileTabs from "@/pages/profile/employee/ProfileTabs";

// Section View Components from employee profile sections
import PersonalTab from "@/pages/profile/employee/sections/PersonalTab";
import ProfessionalTab from "@/pages/profile/employee/sections/ProfessionalTab";
import SkillsTab from "@/pages/profile/employee/sections/SkillsTab";
import ProjectsTab from "@/pages/profile/employee/sections/ProjectsTab";
import EducationTab from "@/pages/profile/employee/sections/EducationTab";
import CertificationsTab from "@/pages/profile/employee/sections/CertificationsTab";
import AwardsTab from "@/pages/profile/employee/sections/AwardsTab";

// Data Hook for main profile details
import { useCandidateProfileById } from "@/pages/candidate-profile/hooks/useCandidateProfile";

import type { MyProfileDetail } from "../api/resume.api";

interface ResumeEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  profileId?: number | string | null;
  initialData?: MyProfileDetail | null;
}

export default function ResumeEditModal({
  isOpen,
  onClose,
  profileId,
  initialData,
}: ResumeEditModalProps) {
  const [activeTab, setActiveTab] = useState<string>("Personal");

  const effectiveId = profileId || initialData?.id;

  // Fetch top-level candidate profile detail for header/basic info
  const { data: profileRes, isLoading } = useCandidateProfileById(
    effectiveId,
    Boolean(isOpen && effectiveId),
  );

  // Reset tab to Personal on modal open
  useEffect(() => {
    if (isOpen) {
      setActiveTab("Personal");
    }
  }, [isOpen]);

  const profileData = profileRes?.data || initialData;

  const renderTabContent = () => {
    if (isLoading && !profileData) {
      return (
        <div className="flex justify-center items-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      );
    }

    switch (activeTab) {
      case "Personal":
        return <PersonalTab profileId={effectiveId} source="other" />;
      case "Experience":
        return <ProfessionalTab profileId={effectiveId} />;
      case "Skills":
        return <SkillsTab profileId={effectiveId} />;
      case "Projects":
        return <ProjectsTab profileId={effectiveId} />;
      case "Education":
        return <EducationTab profileId={effectiveId} />;
      case "Certifications":
        return <CertificationsTab profileId={effectiveId} />;
      case "Awards":
        return <AwardsTab profileId={effectiveId} />;
      default:
        return <PersonalTab profileId={effectiveId} source="other" />;
    }
  };

  const displayName = profileData
    ? `${profileData.first_name || ""} ${profileData.last_name || ""}`.trim() ||
    profileData.job_role ||
    profileData.username
    : "Candidate Profile Details";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] min-h-[90vh] overflow-hidden flex flex-col p-0 gap-0 lg:max-w-5xl">
        <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
          <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-5">
            <span>{displayName}</span>
            {profileData?.job_role && (
              <span className="text-xs font-semibold px-3 py-1 bg-purple-50 text-purple-700 dark:bg-purple-950/50 dark:text-purple-300 rounded-full border border-purple-200/50">
                {profileData.job_role}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            Manage profile information and section details.
          </DialogDescription>
        </DialogHeader>

        {/* Tab Navigation from src/pages/profile/employee/ProfileTabs.tsx */}
        <div className="px-6 pt-4 pb-2 bg-slate-50/50 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-800 shrink-0">
          <ProfileTabs activeTab={activeTab} setActiveTab={setActiveTab} />
        </div>

        {/* Lazy-loaded Section View Components from src/pages/profile/employee/sections */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50/30 dark:bg-slate-950/30">
          {renderTabContent()}
        </div>
      </DialogContent>
    </Dialog>
  );
}
